/**
 * Task Templates Service (story 22770)
 *
 * Client for /api/TaskTemplates (TaskTemplatesController, TaskManager.API).
 * Reads are open; PUT and the version lookup are Admin-role gated server-side.
 * There is no create or delete: the seed owns the catalogue.
 *
 * Aligned to Shared.Models.TaskTemplate.TaskTemplateDto / TaskTemplateUpdateRequest.
 * The API has no global JsonStringEnumConverter, so enums are INTEGERS on the wire.
 */

import { authenticatedFetch } from '../api/authenticatedFetch';

const BASE = '/api/TaskTemplates';

/** Mirrors Shared.Models.TaskTemplate.TaskCategory. */
export enum TaskCategory {
  BusinessModel = 1,
  Legal = 2,
  Branding = 3,
  Operations = 4,
  Marketing = 5,
  Sales = 6,
  Technology = 7,
  CustomerDevelopment = 8,
}

export const TASK_CATEGORY_LABELS: Record<TaskCategory, string> = {
  [TaskCategory.BusinessModel]: 'Business Model',
  [TaskCategory.Legal]: 'Legal',
  [TaskCategory.Branding]: 'Branding',
  [TaskCategory.Operations]: 'Operations',
  [TaskCategory.Marketing]: 'Marketing',
  [TaskCategory.Sales]: 'Sales',
  [TaskCategory.Technology]: 'Technology',
  [TaskCategory.CustomerDevelopment]: 'Customer Development',
};

export const ENRICHMENT_POLICIES = ['JustInTime', 'AtBoardCreation', 'None'] as const;

export interface TaskTemplate {
  id: string;
  category: TaskCategory;
  title: string;
  description: string;
  estimatedHours: number;
  priority: number;
  businessTypes: number[];
  productTypes?: number[] | null;
  serviceTypes?: number[] | null;
  salesChannels?: number[] | null;
  prerequisites?: string[] | null;
  outsourceEstimate?: string | null;
  isActive: boolean;
  domain?: string | null;
  preferredAgentId?: string | null;
  requiredSkills?: string[] | null;
  enrichmentPolicy?: string | null;
  version: number;
}

export interface TaskTemplateListResponse {
  templates: TaskTemplate[];
  totalCount: number;
}

/** Mirrors TaskTemplateUpdateRequest; an omitted (null) field is left unchanged server-side. */
export type TaskTemplateUpdate = Partial<
  Pick<
    TaskTemplate,
    | 'title'
    | 'description'
    | 'estimatedHours'
    | 'priority'
    | 'outsourceEstimate'
    | 'isActive'
    | 'domain'
    | 'preferredAgentId'
    | 'enrichmentPolicy'
  >
>;

async function readError(response: Response): Promise<Error> {
  const body = (await response.json().catch(() => null)) as { message?: string } | null;
  return new Error(body?.message ?? `HTTP ${response.status}: ${response.statusText}`);
}

/** GET /api/TaskTemplates?category=&activeOnly= - activeOnly defaults to false so admins see inactive rows. */
export async function listTaskTemplates(
  filter: { category?: TaskCategory; activeOnly?: boolean } = {},
): Promise<TaskTemplateListResponse> {
  const params = new URLSearchParams();
  if (filter.category !== undefined) params.set('category', String(filter.category));
  params.set('activeOnly', String(filter.activeOnly ?? false));

  const response = await authenticatedFetch(`${BASE}?${params.toString()}`);
  if (!response.ok) throw await readError(response);
  return response.json() as Promise<TaskTemplateListResponse>;
}

/** GET /api/TaskTemplates/{id} */
export async function getTaskTemplate(id: string): Promise<TaskTemplate> {
  const response = await authenticatedFetch(`${BASE}/${id}`);
  if (!response.ok) throw await readError(response);
  return response.json() as Promise<TaskTemplate>;
}

/** PUT /api/TaskTemplates/{id} - snapshots the prior version and bumps `version`. */
export async function updateTaskTemplate(id: string, changes: TaskTemplateUpdate): Promise<TaskTemplate> {
  const response = await authenticatedFetch(`${BASE}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  });
  if (!response.ok) throw await readError(response);
  return response.json() as Promise<TaskTemplate>;
}
